import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CalendarMonth as CalendarMonthIcon,
  EventAvailable as EventAvailableIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import EmptyState from "../common/EmptyState";
import { format, parseISO, isWithinInterval, addDays, differenceInDays, isPast } from "date-fns";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const HolidayList = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const navigate = useNavigate();

  const isHolidayNear = (date) => {
    const today = new Date();
    const holidayDate = parseISO(date);
    const oneWeekLater = addDays(today, 7);
    return isWithinInterval(holidayDate, { start: today, end: oneWeekLater });
  };

  const sortHolidaysByDate = (holidays) => {
    return [...holidays].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  };

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${BASE_URL}/api/holidays`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setHolidays(sortHolidaysByDate(data));
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error fetching holidays");
        }
      } catch (err) {
        console.error("Error fetching holidays:", err);
        setError(err.message || "Error fetching holidays");
      } finally {
        setLoading(false);
      }
    };

    fetchHolidays();
  }, []);

  const handleDelete = async (id) => {
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/holidays/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        setHolidays(holidays.filter((holiday) => holiday._id !== id));
        setConfirmDeleteId(null);
        setError(null);
        setSuccess("Holiday deleted successfully.");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error deleting holiday");
      }
    } catch (err) {
      console.error("Error deleting holiday:", err);
      setError(err.message || "Error deleting holiday");
    }
  };

  const handleAddHoliday = () => {
    navigate("/HolidayForm");
  };

  const cardGradients = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
  ];

  const getNextHoliday = () => {
    const today = new Date();
    return holidays.find((h) => new Date(h.date) >= today);
  };

  const nextHoliday = getNextHoliday();
  const nextHolidayDays = nextHoliday
    ? differenceInDays(parseISO(nextHoliday.date), new Date())
    : null;

  return (
    <PageContainer>
      <PageHeader
        title="Holidays 2025"
        subtitle="Manage company holidays and observances"
        icon={<CalendarMonthIcon />}
        gradient
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddHoliday}
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(8px)",
              borderRadius: "12px",
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "none",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.3)",
                boxShadow: "none",
              },
            }}
          >
            Add Holiday
          </Button>
        }
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={3500}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccess("")}
          severity="success"
          variant="filled"
          sx={{ borderRadius: "8px" }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* Stats Row */}
      {!loading && !error && holidays.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2.5,
            mb: 3,
          }}
        >
          {/* Total Holidays */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid #eef0f6",
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Total Holidays
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "2.2rem", lineHeight: 1.2, mt: 0.5 }}>
                  {holidays.length}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", mt: 0.5 }}>
                  Company holidays this year
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: "rgba(255,255,255,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EventAvailableIcon sx={{ fontSize: 28, color: "#fff" }} />
              </Box>
            </Box>
          </Paper>

          {/* Next Holiday */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid #eef0f6",
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ color: "rgba(0,0,0,0.5)", fontSize: "0.8rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Next Holiday
                </Typography>
                <Typography sx={{ color: "#1a1a2e", fontWeight: 700, fontSize: "1.15rem", lineHeight: 1.3, mt: 0.5 }}>
                  {nextHoliday ? nextHoliday.name : "No upcoming"}
                </Typography>
                {nextHolidayDays !== null && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.45)" }} />
                    <Typography sx={{ color: "rgba(0,0,0,0.5)", fontSize: "0.8rem" }}>
                      {nextHolidayDays === 0 ? "Today!" : `In ${nextHolidayDays} day${nextHolidayDays !== 1 ? "s" : ""}`}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: "rgba(255,255,255,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 28, color: "#1a1a2e" }} />
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 10,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#667eea" }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid #FCA5A5",
            borderRadius: "16px",
            bgcolor: "#FEF2F2",
            mb: 3,
          }}
        >
          <Typography
            sx={{ color: "#DC2626", fontWeight: 500, fontSize: "0.875rem" }}
          >
            {error}
          </Typography>
        </Paper>
      )}

      {/* Holiday Cards Grid */}
      {!loading && !error && holidays.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2.5,
          }}
        >
          {holidays.map((holiday, idx) => {
            const isNear = isHolidayNear(holiday.date);
            const isHolidayPast = isPast(parseISO(holiday.date));
            const gradient = cardGradients[idx % cardGradients.length];
            const holidayDate = parseISO(holiday.date);
            return (
              <Paper
                key={holiday._id}
                elevation={0}
                sx={{
                  border: "1px solid #eef0f6",
                  borderRadius: "16px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.3s ease",
                  opacity: isHolidayPast ? 0.6 : 1,
                  boxShadow: isNear
                    ? "0 0 0 2px rgba(102, 126, 234, 0.3), 0 4px 20px rgba(102, 126, 234, 0.15)"
                    : "0 2px 12px rgba(0,0,0,0.04)",
                  ...(isNear && {
                    animation: "pulseGlow 2s ease-in-out infinite",
                    "@keyframes pulseGlow": {
                      "0%, 100%": {
                        boxShadow: "0 0 0 2px rgba(102, 126, 234, 0.2), 0 4px 20px rgba(102, 126, 234, 0.1)",
                      },
                      "50%": {
                        boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.4), 0 4px 24px rgba(102, 126, 234, 0.25)",
                      },
                    },
                  }),
                  "&:hover": {
                    boxShadow: "0 8px 28px rgba(0,0,0,0.1)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Colored Top Strip */}
                <Box sx={{ height: 4, background: gradient }} />

                <Box sx={{ p: 2.5, flex: 1 }}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                    {/* Date Badge - Mini Calendar */}
                    <Box
                      sx={{
                        minWidth: 54,
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: "1px solid #eef0f6",
                        textAlign: "center",
                        flexShrink: 0,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Box
                        sx={{
                          background: gradient,
                          py: 0.5,
                          px: 1,
                        }}
                      >
                        <Typography sx={{ color: "#fff", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          {format(holidayDate, "MMM")}
                        </Typography>
                      </Box>
                      <Box sx={{ py: 0.75, bgcolor: "#fff" }}>
                        <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", color: "#1a1a2e", lineHeight: 1 }}>
                          {format(holidayDate, "dd")}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.95rem",
                            color: "#1a1a2e",
                            lineHeight: 1.4,
                          }}
                        >
                          {holiday.name}
                        </Typography>
                        {isNear && (
                          <Box
                            sx={{
                              background: "linear-gradient(135deg, #667eea, #764ba2)",
                              color: "#fff",
                              px: 1.25,
                              py: 0.25,
                              borderRadius: "20px",
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                            }}
                          >
                            Upcoming
                          </Box>
                        )}
                      </Box>

                      <Typography sx={{ color: "#8b8fa3", fontSize: "0.8rem", mt: 0.5 }}>
                        {format(holidayDate, "MMMM do, yyyy")}
                      </Typography>

                      <Box
                        sx={{
                          mt: 1,
                          display: "inline-flex",
                          alignItems: "center",
                          bgcolor: "#f5f7fb",
                          borderRadius: "6px",
                          px: 1.25,
                          py: 0.25,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#8b8fa3" }}>
                          {format(holidayDate, "EEEE")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Delete Icon Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    px: 2.5,
                    pb: 2,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setConfirmDeleteId(holiday._id)}
                    sx={{
                      width: 32,
                      height: 32,
                      color: "#DC2626",
                      bgcolor: "#FEF2F2",
                      border: "1px solid #FCA5A5",
                      borderRadius: "8px",
                      "&:hover": {
                        bgcolor: "#FEE2E2",
                        borderColor: "#DC2626",
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Empty State */}
      {!loading && !error && holidays.length === 0 && (
        <EmptyState
          icon={<CalendarMonthIcon />}
          title="No Holidays Found"
          subtitle="You haven't added any holidays yet. Add your first holiday to get started."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddHoliday}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                px: 3,
                py: 1,
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)",
                  boxShadow: "0 6px 24px rgba(102, 126, 234, 0.4)",
                },
              }}
            >
              Add Holiday
            </Button>
          }
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "20px", p: 1, overflow: "hidden" },
        }}
      >
        <Box sx={{ height: 4, background: "linear-gradient(135deg, #DC2626, #f093fb)" }} />
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e", pt: 3 }}
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#5a5f7a", fontSize: "0.875rem" }}>
            Are you sure you want to delete this holiday? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmDeleteId(null)}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 500,
              color: "#1a1a2e",
              borderColor: "#eef0f6",
              px: 2.5,
              "&:hover": {
                borderColor: "#CBD5E1",
                bgcolor: "#f5f7fb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(confirmDeleteId)}
            variant="contained"
            className="delete-holiday-admin"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#DC2626",
              boxShadow: "none",
              px: 2.5,
              "&:hover": {
                bgcolor: "#B91C1C",
                boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default HolidayList;
